import PrintRoundedIcon from "@mui/icons-material/PrintRounded";
import SettingsIcon from "@mui/icons-material/Settings";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Box,
  Divider,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Typography,
  Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import * as chartUtils from "chartUtils";
import * as numbers from "common/numbers";
import DefaultLoader from "components/common/DefaultLoader";
import FormattedDate from "components/common/FormattedDate";
import GoodBadTableCell from "components/common/GoodBadTableCell";
import IconButton from "components/common/IconButton";
import Logo from "components/common/Logo";
import PrimaryExposure from "components/common/PrimaryExposure";
import SmallLoader from "components/common/SmallLoader";
import Visible from "components/common/Visible";
import AggregatedClaimsTable from "components/inputs/claims/AggregatedClaimsTable";
import ClaimsChart from "components/inputs/claims/ClaimsChart";
import Links from "components/inputs/program/links/Links";
import LossFrequencyChart from "components/pricing/lossFrequency/LossFrequencyChart";
import CurrentExposures from "components/summary/program/CurrentExposures";
import CurrentExposuresDialog from "components/summary/program/CurrentExposuresDialog";
import ExposureFlags from "components/summary/program/ExposureFlags";
import HistoricalExposuresTable from "components/summary/program/HistoricalExposuresTable";
import Judgement from "components/summary/program/Judgement";
import LossesByCoverageChart from "components/summary/program/LossesByCoverageChart";
import Notes from "components/summary/program/Notes";
import PrintNote from "components/summary/program/PrintNote";
import PrintRateAdequacy from "components/summary/program/PrintRateAdequacy";
import PrintSettingsDialog from "components/summary/program/PrintSettingsDialog";
import PrintTargetLossRatios from "components/summary/program/PrintTargetLossRatios";
import RecentExposures from "components/summary/program/RecentExposures";
import RegulatoryExemptions from "components/summary/program/RegulatoryExemptions";
import Structure from "components/summary/program/Structure";
import TopClaims from "components/summary/program/TopClaims";
import Tower from "components/summary/program/Tower";
import * as config from "config";
import * as labels from "config.labels";
import { useEffect, useState } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import * as bffService from "services/bffService";
import * as claimsService from "services/claimsService";
import * as staticDataService from "services/staticDataService";
import { cacheImage } from "store/actions/temp/images/imagesActions";
import * as staticDataHooks from "store/hooks/staticDataHooks";
import useDebouncedValue from "store/hooks/useDebouncedValue";
import * as notesStore from "store/notes";
import * as claimsSelectors from "store/selectors/input/claims/claimsSelectors";
import * as exposureSelectors from "store/selectors/input/exposure/exposureSelectors";
import * as flagSelectors from "store/selectors/input/flags/flagsSelectors";
import * as programSelectors from "store/selectors/input/program/programSelectors";
import * as peersSelectors from "store/selectors/peers/peersSelectors";
import * as peopleSelectors from "store/selectors/people/peopleSelectors";
import * as persistenceSelectors from "store/selectors/persistence/persistenceSelectors";
import * as pricingSelectors from "store/selectors/pricing/pricingSelectors";
import * as userSelectors from "store/selectors/user/userSelectors";
import "styles/print.css";

const DollarFormatted = (props) => <GoodBadTableCell prefix={"$"} {...props} />;

const PercentFormatted = (props) => (
  <GoodBadTableCell
    suffix={"%"}
    decimalScale={2}
    fixedDecimalScale
    {...props}
  />
);

const layerlessRow = (submission, translateLabel) => {
  return (
    <TableRow key={`PEERS_ROW_${submission.filename}_LAYERLESS`}>
      <TableCell />
      <TableCell>{submission.insured}</TableCell>
      <TableCell className={"tablecell-inception"}>
        {submission.inception}
      </TableCell>
      <TableCell colSpan={10}>
        {translateLabel(labels.PEER_GROUP_NO_LAYERS, {
          default: "No Layers with positive line size.",
        })}
      </TableCell>
    </TableRow>
  );
};

const loadingRow = (submission) => {
  return (
    <TableRow key={`PEERS_ROW_${submission.filename}_LOADING`}>
      <TableCell />
      <TableCell>{submission.insured}</TableCell>
      <TableCell className={"tablecell-inception"}>
        {submission.inception}
      </TableCell>
      {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
        <TableCell
          align={"right"}
          key={`PEERS_ROW_${submission.filename}_LOADING_${i}`}
        >
          <DefaultLoader
            style={{
              display: "inline-block",
              position: "relative",
              top: "2px",
            }}
            size={16}
          />
        </TableCell>
      ))}
    </TableRow>
  );
};

const layerRow = (
  submission,
  layer,
  layer_index,
  isLast,
  peer_index,
  theme
) => {
  return (
    <TableRow
      key={`PEERS_ROW_${submission.filename}_${layer_index}`}
      style={peer_index === 0 ? { background: theme.brand.gray[800] } : {}}
    >
      <TableCell style={isLast ? {} : { borderBottom: "none" }}>
        {layer_index > 0 ? "" : submission.insured}
      </TableCell>
      <TableCell
        className={"tablecell-inception"}
        style={isLast ? {} : { borderBottom: "none" }}
      >
        {layer_index > 0 ? "" : submission.inception}
      </TableCell>
      <GoodBadTableCell
        cellStyle={isLast ? {} : { borderBottom: "none" }}
        value={layer_index > 0 ? "" : submission.fixedPeerMetricValue}
      />
      <GoodBadTableCell
        cellStyle={isLast ? {} : { borderBottom: "none" }}
        value={layer_index > 0 ? "" : submission.exposure}
      />
      <GoodBadTableCell
        cellStyle={isLast ? {} : { borderBottom: "none" }}
        value={layer_index > 0 ? "" : submission.exposure2}
      />
      <GoodBadTableCell
        cellStyle={isLast ? {} : { borderBottom: "none" }}
        value={layer.lineSize / 1e6}
      />
      <GoodBadTableCell
        cellStyle={isLast ? {} : { borderBottom: "none" }}
        value={layer.limit / 1e6}
      />
      <GoodBadTableCell
        cellStyle={isLast ? {} : { borderBottom: "none" }}
        value={layer.attachment / 1e6}
      />
      <DollarFormatted value={layer.grossPPM} />
      <PercentFormatted value={layer.tp} good={(layer.tp ?? 100) - 100} />
      <PercentFormatted value={layer.totalAcquisition} />
    </TableRow>
  );
};

const ClaimsCharts = ({ hideDevelopment }) => {
  const theme = useTheme();
  const pricingClaimsQuery = useDebouncedValue(
    useSelector(pricingSelectors.selectPricingClaims)
  );
  const translateLabel = staticDataHooks.useTranslator("uiLabels");
  const { currentData, isFetching } = claimsService.useSummaryChartQuery(
    pricingClaimsQuery
  );
  return (
    <div className={"print-claims-charts"}>
      <ClaimsChart
        title={translateLabel(labels.LOSS_FREQUENCY_TITLE, {
          default: "Loss Frequency",
        })}
        chart={currentData?.frequency}
        loading={isFetching}
        dataName={translateLabel(labels.FREQUENCY_DATA_NAME, {
          default: "Frequency",
        })}
        styles={{
          position: "relative",
          width: "100%",
          height: "180px",
        }}
        referenceStyles={{ fontSize: "12px" }}
        titleStyles={{
          fontWeight: "bold",
          display: "inline-block",
          fontSize: "14px",
          marginBottom: "4px",
          color: theme.brand.black,
          textAlign: "center",
        }}
      />
      <ClaimsChart
        title={translateLabel(labels.LOSS_SEVERITY_TITLE, {
          default: "Loss Severity",
        })}
        chart={currentData?.severity}
        loading={isFetching}
        dataName={translateLabel(labels.SEVERITY_DATA_NAME, {
          default: "Severity",
        })}
        ultimateName={
          hideDevelopment
            ? null
            : translateLabel(labels.DEVELOPMENT_AND_INFLATION, {
                default: "Development & Inflation",
              })
        }
        styles={{
          position: "relative",
          width: "100%",
          height: "180px",
        }}
        referenceStyles={{ fontSize: "12px" }}
        averageLabel={(mean) => {
          return `Dev. Avg. to ${mean.year}: ${chartUtils.tickFormatter(
            mean.ultimate
          )}`;
        }}
        titleStyles={{
          fontWeight: "bold",
          display: "inline-block",
          fontSize: "14px",
          marginBottom: "4px",
          color: theme.brand.black,
          textAlign: "center",
        }}
      />
      <ClaimsChart
        title={translateLabel(labels.LOSS_AGGREGATE_TITLE, {
          default: "Loss Aggregate",
        })}
        chart={currentData?.aggregate}
        loading={isFetching}
        dataName={translateLabel(labels.AGGREGATE_DATA_NAME, {
          default: "Aggregate",
        })}
        ultimateName={
          hideDevelopment
            ? null
            : translateLabel(labels.DEVELOPMENT_AND_INFLATION, {
                default: "Development & Inflation",
              })
        }
        averageLabel={(mean) => {
          return `Dev. Avg. to ${mean.year}: ${chartUtils.tickFormatter(
            mean.ultimate
          )}`;
        }}
        referenceStyles={{ fontSize: "12px" }}
        styles={{
          position: "relative",
          width: "100%",
          height: "180px",
        }}
        titleStyles={{
          fontWeight: "bold",
          display: "inline-block",
          fontSize: "14px",
          marginBottom: "4px",
          color: theme.brand.black,
          textAlign: "center",
        }}
      />
    </div>
  );
};

const MetaDetail = ({ label, value, isLoading, tooltip }) => {
  const out = (
    <div className={"print-meta-detail"}>
      <span className={"print-label"}>{label}</span>
      <span className={"print-value"}>
        {isLoading ? <SmallLoader /> : value}
      </span>
    </div>
  );
  return tooltip ? <Tooltip title={tooltip}>{out}</Tooltip> : out;
};

const PriorGWPMetaDetail = ({ submissionId }) => {
  const translateLabel = staticDataHooks.useTranslator("uiLabels");
  const { data, isLoading, isError } = bffService.useCreateInsuredSummaryQuery(
    { submissionId },
    { skip: !submissionId }
  );
  if (isError) {
    return (
      <MetaDetail
        label={translateLabel(labels.PRINT_ITD_GWP, {
          default: "ITD GWP",
        })}
        value={"?"}
        isLoading={isLoading}
        tooltip={translateLabel(labels.PRINT_GWP_ERROR_LOADING_DATA, {
          default: "Error loading data",
        })}
      />
    );
  }

  return (
    <div className={"print-header-section"}>
      <MetaDetail
        label={translateLabel(labels.PRINT_ITD_GWP, {
          default: "ITD GWP",
        })}
        value={numbers.abbreviated(data?.totalPriorBoundPremium) ?? "-"}
        isLoading={isLoading}
        tooltip={numbers.dpString(0)(data?.totalPriorBoundPremium)}
      />
      {(data?.priorBoundPremiumsByYear || []).map(
        ({ inceptionYear, premium }) => (
          <MetaDetail
            key={inceptionYear}
            label={inceptionYear}
            value={numbers.abbreviated(premium)}
            isLoading={false}
            tooltip={numbers.dpString(0)(premium)}
          />
        )
      )}
    </div>
  );
};

const brokerLabel = (broker) => {
  const brokerName = broker?.name;
  const house = broker?.company;
  if (!brokerName && !house) {
    return "-";
  } else if (!house) {
    return brokerName;
  } else if (!brokerName) {
    return house;
  }
  return `${brokerName} (${house})`;
};

const brokerAddress = (broker) => {
  const { data } = staticDataService.useConfigQuery("brokers");
  const brokerConfig = (data?.all ?? []).find((x) => x.id === broker?.id);
  return brokerConfig?.address ?? "-";
};

const CompanyLogo = ({ config }) => {
  const mimeTypes = ["image/png", "image/jpeg"];
  const configValid =
    typeof config?.height === "number" &&
    mimeTypes.includes(config?.mimeType) &&
    typeof config?.data === "string";

  return configValid ? (
    <img
      style={{
        height: numbers.clamp(config?.height, 32, 120),
        borderRadius: numbers.clamp(config?.borderRadius ?? 0, 0, 20),
      }}
      src={`data:${config?.mimeType};base64,${config?.data}`}
      className={"fade-in-slow"}
      alt={"Logo"}
    />
  ) : (
    <></>
  );
};

const AggregatedClaims = () => {
  return (
    <Visible byTag={"<Print>.aggregatedClaimsTable"} defaultVisible={false}>
      <div className={"print-section"}>
        <span className={"print-label"} style={{ paddingBottom: "4px" }}>
          {"Loss History"}
        </span>
        <AggregatedClaimsTable variant={"inline"} />
      </div>
    </Visible>
  );
};

const Print = (props) => {
  const theme = useTheme();
  const peersHeaders = [
    { title: "Insured", tooltip: "", align: "left" },
    { title: "Inception", tooltip: "", align: "left" },
    { title: props.fixedPeerMetricDefn.name, tooltip: "", align: "right" },
    { title: props.exposureMetric?.name, tooltip: "", align: "right" },
    { title: props.exposureMetric2?.name, tooltip: "", align: "right" },
    { title: "Our Limit (m)", tooltip: "", align: "right" },
    { title: "Part Of (m)", tooltip: "", align: "right" },
    { title: "Attach. (m)", tooltip: "", align: "right" },
    { title: "Gross PPM", tooltip: "", align: "right" },
    {
      title: "Target Price (TP)",
      tooltip: config.DEFINITION_TP,
      align: "right",
    },
    {
      title: "TAC",
      tooltip: config.DEFINITION_TAC,
      align: "right",
    },
  ];

  const { data: printConfig } = staticDataService.useConfigQuery(
    "print_config"
  );
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isCurrentExposuresOpen, setIsCurrentExposuresOpen] = useState(false);
  const dispatch = useDispatch();

  const translateLabel = staticDataHooks.useTranslator("uiLabels");

  const remoteImages = useSelector((state) => state.input.images?.remote ?? {});
  const imageCache = useSelector((state) => state.temp.images?.cache ?? {});
  const insuredLogoImage = props.insuredLogo?.localId
    ? remoteImages?.[props.insuredLogo.localId]
    : null;
  const insuredLogoCachedUrl = insuredLogoImage
    ? imageCache?.[insuredLogoImage.submissionId]?.[insuredLogoImage.imageId]
    : null;

  useEffect(() => {
    if (
      insuredLogoImage?.submissionId &&
      insuredLogoImage?.imageId &&
      insuredLogoCachedUrl == null
    ) {
      dispatch(
        cacheImage(
          insuredLogoImage.submissionId,
          insuredLogoImage.imageId,
          insuredLogoImage.imageType
        )
      );
    }
  }, [
    dispatch,
    insuredLogoCachedUrl,
    insuredLogoImage?.imageId,
    insuredLogoImage?.imageType,
    insuredLogoImage?.submissionId,
  ]);

  const insuredLogoUrl = insuredLogoImage
    ? insuredLogoCachedUrl ?? "/loading.gif"
    : null;

  const componentOnStateVisibility = useSelector(
    userSelectors.getComponentOnStateVisibility
  );
  const hasLinks = useSelector(
    (state) => (state.input?.program?.links ?? []).length > 0
  );

  const hasLossFrequencyChart =
    props.userConfig.componentVisibility?.["<Print>.lossFrequencyChart"] ??
    false;

  const lossesByCoverageConfigVisible =
    props.userConfig.componentVisibility?.["<Print>.lossesByCoverageChart"] ??
    false;
  const lossesByCoverageStateVisibility =
    componentOnStateVisibility?.["<Print>.lossesByCoverageChart"] ??
    (() => lossesByCoverageConfigVisible);
  const isLossesByCoverageVisible =
    useSelector(lossesByCoverageStateVisibility) ??
    lossesByCoverageConfigVisible;

  const linksConfigVisible =
    props.userConfig.componentVisibility?.["<Print>.links"] ?? false;
  const linksStateVisibility =
    componentOnStateVisibility?.["<Print>.links"] ?? (() => linksConfigVisible);
  const isLinksVisible =
    useSelector(linksStateVisibility) ?? linksConfigVisible;

  return (
    <>
      <PrintSettingsDialog
        open={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
      />
      <CurrentExposuresDialog
        open={isCurrentExposuresOpen}
        onClose={() => setIsCurrentExposuresOpen(false)}
      />
      <div className={"print"}>
        <div className={"page"}>
          <div className={"print-insured-header"}>
            <Grid
              container
              justifyContent={"space-between"}
              alignItems={"flex-start"}
            >
              <Stack alignItems={"flex-start"}>
                <CompanyLogo config={printConfig?.logo ?? {}} />
                <h1>{props.insured}</h1>
              </Stack>
              <Stack direction={"row"} alignItems={"flex-start"} spacing={1}>
                <Stack
                  direction={"row"}
                  alignItems={"center"}
                  displayPrint={"none"}
                >
                  <IconButton
                    variant={"bright"}
                    scale={"small"}
                    icon={SettingsIcon}
                    tooltip={"Settings"}
                    onClick={() => setIsSettingsOpen(true)}
                  />
                  <IconButton
                    variant={"bright"}
                    scale={"small"}
                    icon={PrintRoundedIcon}
                    tooltip={"Print"}
                    onClick={() => window.print()}
                  />
                </Stack>
                <Visible byTag={"<Print>.insuredLogo"} defaultVisible={true}>
                  {insuredLogoUrl && (
                    <Box
                      component={"img"}
                      src={insuredLogoUrl}
                      alt={"Insured logo"}
                      className={"fade-in-slow"}
                      sx={{
                        maxHeight: 90,
                        maxWidth: 200,
                        objectFit: "contain",
                        borderRadius: 2,
                      }}
                    />
                  )}
                </Visible>
              </Stack>
            </Grid>
            <span className={"print-risk-description"}>
              {props.riskDescription}
            </span>
          </div>

          <div className={"print-header-section"}>
            <Visible byTag={"<Print>.meta.reference"} defaultVisible>
              <MetaDetail
                label={translateLabel(labels.PRINT_META_REF, {
                  default: "Ref",
                })}
                value={props.reference || "-"}
                visibilityTag={"<Print>.meta."}
              />
            </Visible>
            <Visible byTag={"<Print>.meta.inception"} defaultVisible>
              <MetaDetail
                label={translateLabel(labels.PRINT_META_INCEPTION, {
                  default: "Inception",
                })}
                value={
                  <FormattedDate
                    configKey={"print-inception"}
                    value={props.inception}
                  />
                }
              />
            </Visible>
            <Visible byTag={"<Print>.meta.renewal"} defaultVisible>
              <MetaDetail
                label={translateLabel(labels.PRINT_META_RENEWAL, {
                  default: "Renewal",
                })}
                value={props.isRenewal ? "YES" : "NO"}
              />
            </Visible>
            <Visible byTag={"<Print>.meta.industryClass"} defaultVisible>
              <MetaDetail
                label={translateLabel(labels.PRINT_META_INDUSTRY_CLASS, {
                  default: "Industry Class",
                })}
                value={props.industryClass}
              />
            </Visible>
            <Visible byTag={"<Print>.meta.preferredUnderwriter"} defaultVisible>
              <MetaDetail
                label={translateLabel(labels.PRINT_META_UW, {
                  default: "UW",
                })}
                value={props.preferredUnderwriter?.name || "-"}
              />
            </Visible>
            <Visible byTag={"<Print>.meta.brokerage"} defaultVisible>
              <MetaDetail
                label={translateLabel(labels.PRINT_META_BROKERAGE, {
                  default: "Brokerage",
                })}
                value={props.brokerage != null ? `${props.brokerage}%` : null}
              />
            </Visible>
            <Visible byTag={"<Print>.meta.broker"} defaultVisible>
              <MetaDetail
                label={translateLabel(labels.PRINT_META_BROKER, {
                  default: "Broker",
                })}
                value={brokerLabel(props.broker)}
              />
            </Visible>
            <Visible
              byTag={"<Print>.meta.brokerAddress"}
              defaultVisible={false}
            >
              <MetaDetail
                label={translateLabel(labels.PRINT_META_BROKER_ADDRESS, {
                  default: "Broker Address",
                })}
                value={brokerAddress(props.broker)}
              />
            </Visible>
            <Visible
              byTag={"<Print>.meta.insuredAddress"}
              defaultVisible={false}
            >
              <MetaDetail
                label={translateLabel(labels.PRINT_META_INSURED_ADDRESS, {
                  default: "Insured Address",
                })}
                value={props.insuredAddress}
              />
            </Visible>
          </div>

          <Divider className={"print-divider"} />

          {props.isRenewal && (
            <Visible byTag={"<Print>.summary.priorGwp"} defaultVisible={true}>
              <PriorGWPMetaDetail submissionId={props.submissionId} />
              <Divider className={"print-divider"} />
            </Visible>
          )}

          <PrintNote
            title={translateLabel(labels.PRINT_RISK_SUMMARY_TITLE, {
              default: "Risk Summary",
            })}
            markdown={props.riskSummary}
          />

          {props.riskSummary && <Divider className={"print-divider"} />}

          {props.usingAggregatedClaimsInput && <AggregatedClaims />}

          {props.hasClaims && (
            <>
              {(isLossesByCoverageVisible || isLinksVisible) && (
                <Box className={"print-section print-links-and-coverage"}>
                  <Visible
                    byTag={"<Print>.lossesByCoverageChart"}
                    defaultVisible={false}
                  >
                    <LossesByCoverageChart />
                  </Visible>
                  {hasLinks && (
                    <Visible byTag={"<Print>.links"} defaultVisible={false}>
                      <Links readOnly />
                    </Visible>
                  )}
                </Box>
              )}

              <Visible byTag={"<Print>.claimsCharts"} defaultVisible>
                <ClaimsCharts
                  hideDevelopment={printConfig?.hideDevelopment ?? false}
                />
              </Visible>

              <AggregatedClaims />

              <Visible byTag={"<Print>.topClaims"} defaultVisible>
                <div
                  className={
                    hasLossFrequencyChart
                      ? "print-claims-table-and-freq-chart"
                      : ""
                  }
                >
                  <TopClaims fullWidth={!hasLossFrequencyChart} />

                  <Visible
                    byTag={"<Print>.lossFrequencyChart"}
                    defaultVisible={false}
                  >
                    <div>
                      <LossFrequencyChart
                        credibleTitle={true}
                        hideLegend={true}
                        chartMargins={{
                          top: 0,
                          right: 30,
                          left: 0,
                          bottom: 0,
                        }}
                        styles={{
                          position: "relative",
                          width: "100%",
                          paddingBottom: "220px",
                          textAlign: "center",
                          left: "-40px",
                        }}
                        titleStyles={{
                          fontWeight: "bold",
                          display: "inline-block",
                          fontSize: "14px",
                          marginBottom: "4px",
                          color: theme.brand.black,
                          textAlign: "center",
                        }}
                      />
                      <ul
                        style={{
                          listStyleType: "square",
                          fontWeight: "bold",
                          fontSize: "11px",
                        }}
                      >
                        <li style={{ color: theme.brand.chart.primary }}>
                          {"Reported"}
                        </li>
                        <li style={{ color: theme.brand.chart.secondary }}>
                          {"Expected Development"}
                        </li>
                        <li style={{ color: theme.brand.chart.reference }}>
                          {"Model"}
                        </li>
                        <li style={{ color: "#A0A6AD" }}>
                          {"Insured Selected # of Losses"}
                        </li>
                        <li style={{ color: "#CC5ED6" }}>
                          {"Model/Claims Blend"}
                        </li>
                        <li style={{ color: "#3A4148" }}>
                          {"Losses for Pricing"}
                        </li>
                      </ul>
                    </div>
                  </Visible>
                </div>
              </Visible>
            </>
          )}

          {!props.hasClaims && hasLinks && (
            <Visible byTag={"<Print>.links"} defaultVisible={false}>
              <Box sx={{ marginBottom: "16px" }}>
                <Links readOnly />
              </Box>
            </Visible>
          )}

          <Visible byTag={"<Print>.tower"} defaultVisible={false}>
            <Tower />
          </Visible>

          <Visible byTag={"<Print>.regulatoryExemptions"}>
            <RegulatoryExemptions />
          </Visible>
        </div>

        <div className={"page"}>
          <Visible byTag={"<Print>.primaryExposure"} defaultVisible={false}>
            <Grid
              container
              spacing={config.GRID_SPACING}
              className={"print-section"}
            >
              <Grid item xs={12}>
                <span className={"print-label"}>{"Primary Exposure"}</span>
                <PrimaryExposure hideInput />
              </Grid>
            </Grid>
          </Visible>

          <Visible byTag={"<Print>.currentExposures"} defaultVisible={false}>
            <Grid item xs={12}>
              <Stack direction={"row"} alignItems={"center"} gap={1}>
                <span className={"print-label"}>
                  {translateLabel(labels.PRINT_CURRENT_EXPOSURES, {
                    default: "Current Exposures",
                  })}
                </span>
                <IconButton
                  variant={"filled"}
                  scale={"small"}
                  icon={VisibilityIcon}
                  tooltip={"Select exposures"}
                  onClick={() => setIsCurrentExposuresOpen(true)}
                  sx={{ bottom: "2px" }}
                  className={"no-print"}
                />
              </Stack>
              <Stack
                className={"print-section"}
                direction={"row"}
                alignItems={"flex-start"}
                justifyContent={"flex-start"}
                gap={3}
              >
                <Box sx={{ flex: "1 1 0", minWidth: 0 }}>
                  <CurrentExposures showPrimaryExposure />
                </Box>
              </Stack>
            </Grid>
          </Visible>

          <Visible byTag={"<Print>.recentExposures"} defaultVisible={false}>
            <Grid item xs={12}>
              <span className={"print-label"}>
                {translateLabel(labels.PRINT_RECENT_EXPOSURES, {
                  default: "Historical Exposures",
                })}
              </span>
              <RecentExposures />
            </Grid>
          </Visible>

          <Visible byTag={"<Print>.historicalExposuresTable"} defaultVisible>
            <Grid
              container
              spacing={config.GRID_SPACING}
              className={"print-section"}
            >
              <HistoricalExposuresTable updating={props.rowsUpdating} />
            </Grid>
          </Visible>

          <Visible byTag={"<Print>.rateAdequacy"} defaultVisible={false}>
            <Grid
              container
              spacing={config.GRID_SPACING}
              className={"print-section print-no-break"}
            >
              <PrintRateAdequacy />
            </Grid>
          </Visible>

          <Visible byTag={"<Print>.peerPricing"} defaultVisible={false}>
            <Grid
              container
              spacing={config.GRID_SPACING}
              className={"print-section"}
            >
              <Grid item xs={12}>
                <span className={"print-label"}>
                  {"Peer Comparison / Pricing"}
                </span>
                <TableContainer>
                  <Table className={"print-peer-comparison-table"}>
                    <TableHead>
                      <TableRow>
                        {peersHeaders.map((header, i) => (
                          <TableCell
                            key={`REVIEW_TOOLTIP_TABLE_CELL_${i}`}
                            align={header.align}
                          >
                            {header.title}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {props.peerData.map((submission, peer_index) => {
                        if (!submission.loaded) {
                          return loadingRow(submission, peer_index);
                        }
                        if (submission.layers.length === 0) {
                          return layerlessRow(submission, translateLabel);
                        }
                        return submission.layers.map((layer, layer_index) => {
                          return layerRow(
                            submission,
                            layer,
                            layer_index,
                            layer_index + 1 === submission.layers.length,
                            peer_index,
                            theme
                          );
                        });
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          </Visible>

          <Grid
            container
            direction={"column"}
            spacing={config.GRID_SPACING}
            className={"print-section"}
          >
            <Visible byTag={"<Print>.uwJudgments"} defaultVisible={true}>
              {props.judgments.length > 1 && (
                <Judgement
                  judgements={props.judgments}
                  judgementFactorPretty={props.judgmentFactorPretty}
                />
              )}
            </Visible>
            <Visible byTag={"<Print>.exposureFlags"} defaultVisible={true}>
              {props.exposures.length > 0 && (
                <ExposureFlags exposures={props.exposures} />
              )}
            </Visible>
          </Grid>
        </div>

        <div className={"print-section"}>
          <Visible byTag={"<Print>.structure"} defaultVisible={false}>
            <div className={"print-section print-no-break"}>
              <span className={"print-label"} style={{ marginBottom: "8px" }}>
                {translateLabel(labels.PRINT_STRUCTURE, {
                  default: "Program Structure",
                })}
              </span>
              <Structure />
            </div>
          </Visible>

          <Notes
            summaryNotes={props.summaryNotes}
            otherNotes={props.otherNotes}
            peerReview={props.peerReview}
            spacing={config.GRID_SPACING}
          />

          <Visible byTag={"<Print>.targetLossRatios"} defaultVisible={false}>
            <Grid
              container
              spacing={config.GRID_SPACING}
              className={"print-section print-no-break"}
            >
              <PrintTargetLossRatios />
            </Grid>
          </Visible>
        </div>

        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <div className={"print-scaled"}>
            <Logo variant={"poweredBy"} />
          </div>
          <Typography variant={"caption"} color={"lightslategray"}>
            {"cactusrisk.com"}
          </Typography>
        </Stack>
      </div>
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    inception: state.input.program.inception,
    insured: state.input.program.insured,
    insuredLogo: state.input.program.insuredLogo,
    insuredAddress: state.input.program.insuredAddress,
    riskDescription: state.input.program.riskDescription,
    riskSummary: state.input.program.riskSummary,
    reference: state.input.program.reference,
    brokerage: state.input.program.brokerage,
    preferredUnderwriter: peopleSelectors.selectPreferredUnderwriter(state),
    broker: peopleSelectors.selectBroker(state),
    industryClass: programSelectors.getPrettyIndustryClass(state),
    summaryNotes: state.summary.notes,
    otherNotes: {
      program: state.input.program.notes,
      claims: state.input.claims.note,
      exposure: state.input.exposure.note,
      scenarios: state.input.scenarios.note,
      lossDevelopment: notesStore.selectNoteText("lossDevelopment")(state),
    },
    peerReview: state.summary.peerReview ?? {},
    rows: exposureSelectors.getExposureList(state),
    tower: pricingSelectors.getTower(state),
    judgments: flagSelectors.getNonEmptyJudgments(state),
    judgmentFactorPretty: flagSelectors.getJudgmentFactorPretty(state),
    exposures: flagSelectors.getExposures(state),
    expectedLosses: pricingSelectors.getNumberOfLossesForPricingPretty(state),
    insuredWeight: state.pricing.insuredWeight,
    usingAggregatedClaimsInput:
      claimsSelectors.selectAggregatedClaims(state).manualMode ?? false,
    hasClaims: !claimsSelectors.isClaimsQueryEmpty(state),
    peerData: peersSelectors.getPeers(state),
    exposureMetric: state.peers.exposureMetric,
    exposureMetric2: state.peers.exposureMetric2,
    fixedPeerMetricDefn: peersSelectors.selectFixedPeerMetricDefn(state),
    isRenewal: !!state.input.program.priorSubmission,
    userConfig: userSelectors.selectUserConfig(state),
    submissionId: persistenceSelectors.selectSubmissionId(state),
  };
};

export default connect(mapStateToProps)(Print);
